import { useEffect, useRef, useState } from 'react';
import Button from './Button';
import Input from './Input';

const QRScanner = ({ onScan, onError, onClose }) => {
  const html5QrCodeRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualInput, setManualInput] = useState(false);
  const [manualQr, setManualQr] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const Html5QrcodeRef = useRef(null);
  const isProcessingRef = useRef(false); // Флаг для предотвращения повторных обработок
  const lastScannedQrRef = useRef(null); // Последний отсканированный QR-код

  const stopScanning = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        setIsScanning(false);
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    const startScanning = async () => {
      try {
        // Dynamically import html5-qrcode to ensure it's loaded correctly
        if (!Html5QrcodeRef.current) {
          const html5QrcodeModule = await import('html5-qrcode');
          // Try different export patterns
          Html5QrcodeRef.current = html5QrcodeModule.Html5Qrcode || 
                                   html5QrcodeModule.default?.Html5Qrcode ||
                                   html5QrcodeModule.default ||
                                   html5QrcodeModule;
          
          if (!Html5QrcodeRef.current) {
            throw new Error('Failed to load Html5Qrcode library');
          }
        }

        const Html5Qrcode = Html5QrcodeRef.current;
        
        // Импортируем форматы поддержки
        let Html5QrcodeSupportedFormats;
        try {
          const formatsModule = await import('html5-qrcode');
          Html5QrcodeSupportedFormats = formatsModule.Html5QrcodeSupportedFormats || 
                                        formatsModule.default?.Html5QrcodeSupportedFormats;
        } catch (e) {
          console.warn('Could not load supported formats, using default');
        }

        // Check if Html5Qrcode is available
        if (!Html5Qrcode || typeof Html5Qrcode.getCameras !== 'function') {
          throw new Error('Библиотека Html5Qrcode не загружена корректно');
        }

        // Check if camera is available
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length === 0) {
          throw new Error('Камера не найдена');
        }

        // Сохраняем список камер для переключения
        setAvailableCameras(devices);

        const html5QrCode = new Html5Qrcode('qr-reader');
        html5QrCodeRef.current = html5QrCode;

        // Try to find back camera first, fallback to any camera
        let cameraId = null;
        let initialCameraIndex = 0;
        const backCamera = devices.find((device, index) => {
          const label = device.label.toLowerCase();
          if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
            initialCameraIndex = index;
            return true;
          }
          return false;
        });
        
        if (backCamera) {
          cameraId = backCamera.id;
          setCurrentCameraIndex(initialCameraIndex);
        } else if (devices.length > 0) {
          cameraId = devices[0].id;
          setCurrentCameraIndex(0);
        }

        // Улучшенная конфигурация для лучшего сканирования
        // Определяем размер области сканирования в зависимости от размера экрана
        const getViewportSize = () => {
          const width = window.innerWidth;
          const height = window.innerHeight;
          // Используем 80% от меньшей стороны экрана, но не меньше 250px и не больше 400px
          const size = Math.min(Math.max(Math.min(width, height) * 0.8, 250), 400);
          return Math.floor(size);
        };

        const qrboxSize = getViewportSize();

        // Для Safari/iOS используем специальную конфигурацию
        const config = isSafari || isIOS ? {
          fps: 10, // Увеличено с 5 для более быстрого сканирования
          qrbox: { width: qrboxSize, height: qrboxSize },
          aspectRatio: 1.0,
          // Улучшенные настройки видео для лучшего качества
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1920, min: 1280 }, // Увеличено разрешение
            height: { ideal: 1080, min: 720 },
            focusMode: 'continuous', // Непрерывная фокусировка
            advanced: [
              { focusMode: 'continuous' },
              { exposureMode: 'continuous' },
              { whiteBalanceMode: 'continuous' }
            ]
          },
          // Дополнительные параметры для лучшего распознавания
          ...(Html5QrcodeSupportedFormats ? {
            formatsToSupport: [
              Html5QrcodeSupportedFormats.QR_CODE,
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8,
              Html5QrcodeSupportedFormats.UPC_A,
              Html5QrcodeSupportedFormats.UPC_E,
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.CODE_39,
              Html5QrcodeSupportedFormats.CODE_93,
              Html5QrcodeSupportedFormats.CODABAR,
              Html5QrcodeSupportedFormats.ITF,
              Html5QrcodeSupportedFormats.DATA_MATRIX,
              Html5QrcodeSupportedFormats.AZTEC
            ]
          } : {}),
          // Улучшенные параметры сканирования
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          },
          // Увеличиваем таймаут для сложных QR-кодов
          rememberLastUsedCamera: true,
        } : {
          // Для других браузеров используем оптимизированную конфигурацию
          fps: 15, // Увеличено для более быстрого сканирования
          qrbox: { width: qrboxSize, height: qrboxSize },
          aspectRatio: 1.0,
          // Улучшенные настройки видео
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            focusMode: 'continuous',
          },
          // Поддержка различных форматов
          ...(Html5QrcodeSupportedFormats ? {
            formatsToSupport: [
              Html5QrcodeSupportedFormats.QR_CODE,
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8,
              Html5QrcodeSupportedFormats.UPC_A,
              Html5QrcodeSupportedFormats.UPC_E,
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.CODE_39,
              Html5QrcodeSupportedFormats.CODE_93,
              Html5QrcodeSupportedFormats.CODABAR,
              Html5QrcodeSupportedFormats.ITF,
              Html5QrcodeSupportedFormats.DATA_MATRIX,
              Html5QrcodeSupportedFormats.AZTEC
            ]
          } : {}),
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          },
          rememberLastUsedCamera: true,
        };

        // Улучшенный обработчик успешного сканирования
        const onScanSuccess = (decodedText, decodedResult) => {
          // Предотвращаем повторную обработку того же QR-кода
          if (isProcessingRef.current) {
            console.log('⏸️ QR scan already processing, ignoring duplicate');
            return;
          }
          
          // Игнорируем повторные сканирования того же QR-кода
          if (lastScannedQrRef.current === decodedText) {
            console.log('⏸️ Same QR code scanned again, ignoring');
            return;
          }
          
          console.log('✅ QR Code scanned:', decodedText);
          
          // Устанавливаем флаги для предотвращения повторных обработок
          isProcessingRef.current = true;
          lastScannedQrRef.current = decodedText;
          
          // Останавливаем сканирование сразу, чтобы предотвратить повторные вызовы
          stopScanning().then(() => {
            // Небольшая задержка для визуальной обратной связи
            setTimeout(() => {
              onScan(decodedText);
            }, 100);
          }).catch((err) => {
            console.error('Error stopping scanner:', err);
            // Все равно вызываем onScan даже если остановка не удалась
            onScan(decodedText);
          });
        };

        // Улучшенный обработчик ошибок сканирования
        const onScanFailure = (errorMessage) => {
          // Игнорируем частые ошибки во время сканирования
          // Но логируем для отладки
          if (errorMessage && !errorMessage.includes('NotFoundException')) {
            console.debug('Scan attempt:', errorMessage);
          }
        };

        await html5QrCode.start(
          cameraId || { facingMode: 'environment' },
          config,
          onScanSuccess,
          onScanFailure
        );

        setIsScanning(true);
        setError(null);
      } catch (err) {
        console.error('Error starting scanner:', err);
        const errorMsg = err.message || 'Не удалось запустить камеру';
        setError(errorMsg);
        setIsScanning(false);
        
        // Show manual input option if camera fails
        if (err.message?.includes('NotAllowedError') || 
            err.message?.includes('Permission denied') ||
            err.message?.includes('camera') ||
            err.message?.includes('Html5Qrcode')) {
          setError('Доступ к камере запрещен или недоступен. Используйте ручной ввод.');
        }
        
        onError?.(errorMsg);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Сбрасываем флаги при запуске нового сканирования
      isProcessingRef.current = false;
      lastScannedQrRef.current = null;
      startScanning();
    }, 100);

    return () => {
      clearTimeout(timer);
      stopScanning();
      // Сбрасываем флаги при размонтировании
      isProcessingRef.current = false;
      lastScannedQrRef.current = null;
    };
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const qrValue = manualQr.trim();
    if (qrValue && !isProcessingRef.current) {
      isProcessingRef.current = true;
      lastScannedQrRef.current = qrValue;
      onScan(qrValue);
      setManualInput(false);
      setManualQr('');
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    try {
      await stopScanning();
      
      // Сбрасываем флаги при переключении камеры
      isProcessingRef.current = false;
      lastScannedQrRef.current = null;
      
      const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
      setCurrentCameraIndex(nextIndex);
      const nextCamera = availableCameras[nextIndex];
      
      // Перезапускаем сканирование с новой камерой
      const html5QrCode = html5QrCodeRef.current;
      if (html5QrCode && nextCamera) {
        const qrboxSize = Math.min(Math.max(Math.min(window.innerWidth, window.innerHeight) * 0.8, 250), 400);
        const config = {
          fps: isIOSDevice ? 10 : 15,
          qrbox: { width: qrboxSize, height: qrboxSize },
          aspectRatio: 1.0,
        };

        const onScanSuccess = (decodedText) => {
          // Предотвращаем повторную обработку того же QR-кода
          if (isProcessingRef.current) {
            console.log('⏸️ QR scan already processing, ignoring duplicate');
            return;
          }
          
          // Игнорируем повторные сканирования того же QR-кода
          if (lastScannedQrRef.current === decodedText) {
            console.log('⏸️ Same QR code scanned again, ignoring');
            return;
          }
          
          console.log('✅ QR Code scanned:', decodedText);
          
          // Устанавливаем флаги для предотвращения повторных обработок
          isProcessingRef.current = true;
          lastScannedQrRef.current = decodedText;
          
          // Останавливаем сканирование сразу
          stopScanning().then(() => {
            setTimeout(() => {
              onScan(decodedText);
            }, 100);
          }).catch((err) => {
            console.error('Error stopping scanner:', err);
            onScan(decodedText);
          });
        };

        const onScanFailure = (errorMessage) => {
          if (errorMessage && !errorMessage.includes('NotFoundException')) {
            console.debug('Scan attempt:', errorMessage);
          }
        };

        await html5QrCode.start(
          nextCamera.id,
          config,
          onScanSuccess,
          onScanFailure
        );
        
        setIsScanning(true);
        setError(null);
      }
    } catch (err) {
      console.error('Error switching camera:', err);
      setError('Не удалось переключить камеру');
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="bg-white p-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Сканирование QR-кода</h2>
        <div className="flex gap-2">
          {availableCameras.length > 1 && !manualInput && (
            <button
              onClick={switchCamera}
              className="text-blue-600 font-semibold text-sm"
              title="Переключить камеру"
            >
              🔄
            </button>
          )}
          <button
            onClick={() => {
              setManualInput(!manualInput);
            }}
            className="text-blue-600 font-semibold text-sm"
          >
            {manualInput ? 'Камера' : 'Вручную'}
          </button>
          <button
            onClick={() => {
              stopScanning();
              onClose();
            }}
            className="text-blue-600 font-semibold"
          >
            Закрыть
          </button>
        </div>
      </div>

      {manualInput ? (
        <div className="flex-1 flex items-center justify-center bg-gray-900 p-4">
          <div className="w-full max-w-md bg-white rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Введите QR-код вручную</h3>
            <form onSubmit={handleManualSubmit}>
              <Input
                label="QR-код"
                value={manualQr}
                onChange={(e) => setManualQr(e.target.value)}
                placeholder="Введите QR-код"
                autoFocus
                required
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={!manualQr.trim()}
                >
                  Отправить
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setManualInput(false);
                    setManualQr('');
                  }}
                >
                  Отмена
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-yellow-400">⚠️</span>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-yellow-700">{error}</p>
                  <div className="mt-2">
                    <button
                      onClick={() => setManualInput(true)}
                      className="text-sm font-medium text-yellow-800 underline"
                    >
                      Использовать ручной ввод
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 flex items-center justify-center relative">
            {!isScanning && !error && (
              <div className="text-white text-center absolute z-10">
                <p className="mb-4 text-lg">Запуск камеры...</p>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              </div>
            )}
            <div id="qr-reader" className="w-full max-w-md"></div>
            {isScanning && (
              <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm">
                📷 Наведите камеру на QR-код
              </div>
            )}
          </div>

          <div className="bg-white p-4 text-center text-sm text-gray-600">
            {isSafari || isIOS ? (
              <p>Наведите камеру на QR-код. Если камера не работает, используйте ручной ввод.</p>
            ) : (
              <p>Наведите камеру на QR-код</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QRScanner;
